import { QueryClient, Query } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { z } from "zod";

const cachedQuerySchema = z.object({
  queryKey: z.array(z.unknown()),
});

const cachedStateSchema = z.object({
  clientState: z.object({
    queries: z.array(cachedQuerySchema),
    mutations: z.array(z.unknown()),
  }),
  timestamp: z.number(),
  buster: z.string(),
});

interface ProcessLike {
  env?: {
    NODE_ENV?: string;
  };
}

declare const process: ProcessLike | undefined;

// Create a persister for React Query cache
const queryPersister: ReturnType<typeof createAsyncStoragePersister> =
  createAsyncStoragePersister({
  storage: window.localStorage,
  key: "bitsentry-query-cache",
  throttleTime: 1000,
  });

// Cache configuration
export const CACHE_CONFIG = {
  // Default cache times
  defaultStaleTime: 5 * 60 * 1000, // 5 minutes
  defaultCacheTime: 30 * 60 * 1000, // 30 minutes

  // Specific cache times by query type
  queryStaleTime: {
    // Long cache for mostly static data
    users: 30 * 60 * 1000, // 30 minutes
    settings: 60 * 60 * 1000, // 1 hour
    integrations: 15 * 60 * 1000, // 15 minutes
    reportTemplates: 60 * 60 * 1000, // 1 hour

    // Medium cache for semi-dynamic data
    agents: 5 * 60 * 1000, // 5 minutes
    vulnerabilities: 5 * 60 * 1000, // 5 minutes
    reports: 10 * 60 * 1000, // 10 minutes

    // Short cache for real-time data
    notifications: 1 * 60 * 1000, // 1 minute
    threats: 2 * 60 * 1000, // 2 minutes
    analytics: 2 * 60 * 1000, // 2 minutes
    activity: 1 * 60 * 1000, // 1 minute

    // Very short cache for critical real-time data
    metrics: 30 * 1000, // 30 seconds
    alerts: 30 * 1000, // 30 seconds
  },

  // Cache time multipliers for different environments
  cacheMultiplier: {
    development: 0.5, // Shorter cache in dev
    staging: 1,
    production: 1.5, // Longer cache in production
  },
};

function getQueryStaleTime(queryType: string): number | undefined {
  const match = Object.entries(CACHE_CONFIG.queryStaleTime).find(
    ([configuredQueryType]) => configuredQueryType === queryType,
  );

  if (match === undefined) {
    return undefined;
  }

  return match[1];
}

function getEnvironmentCacheMultiplier(environment: string): number {
  switch (environment) {
    case "development": {
      return CACHE_CONFIG.cacheMultiplier.development;
    }
    case "staging": {
      return CACHE_CONFIG.cacheMultiplier.staging;
    }
    case "production": {
      return CACHE_CONFIG.cacheMultiplier.production;
    }
    default: {
      return 1;
    }
  }
}

function getRuntimeEnvironment(): string {
  if (typeof process === "undefined") {
    return "development";
  }

  return process.env?.NODE_ENV ?? "development";
}

// Get stale time for a specific query type
export function getStaleTime(queryType: string): number {
  const baseTime =
    getQueryStaleTime(queryType) ?? CACHE_CONFIG.defaultStaleTime;
  const multiplier = getEnvironmentCacheMultiplier(getRuntimeEnvironment());

  return baseTime * multiplier;
}

// Queries that should NOT be persisted (auth-sensitive data)
const NON_PERSISTABLE_QUERIES = [
  "authRouter.getCurrentUser",
  "authRouter.getProfile",
  "auth",
  "currentUser",
  "profile",
];

function getQueryKeyText(key: unknown): string | undefined {
  if (typeof key === "string") {
    return key;
  }

  if (!Array.isArray(key)) {
    return undefined;
  }

  return key.join(".");
}

function isNonPersistableQueryKey(key: unknown): boolean {
  const keyText = getQueryKeyText(key);

  if (keyText === undefined) {
    return false;
  }

  return NON_PERSISTABLE_QUERIES.some((nonPersistableQuery) =>
    keyText.includes(nonPersistableQuery),
  );
}

// Check if a query should be persisted
const shouldPersistQuery = (query: Query): boolean => {
  return !query.queryKey.some(isNonPersistableQueryKey);
};

// Setup persistent query client
export function setupPersistentQueryClient(queryClient: QueryClient) {
  void persistQueryClient({
    queryClient,
    persister: queryPersister,
    maxAge: 24 * 60 * 60 * 1000, // 24 hours max age
    hydrateOptions: {
      // Hydrate options configuration
    },
    dehydrateOptions: {
      // Only persist successful queries that are not auth-related
      shouldDehydrateQuery: (query) => {
        return query.state.status === "success" && shouldPersistQuery(query);
      },
    },
  });
}

// Cache utilities
export const cacheUtils = {
  // Clear all cached data
  clearAll: () => {
    localStorage.removeItem("bitsentry-query-cache");
  },

  // Clear specific query cache
  clearQuery: (queryKey: string[]) => {
    const cache = localStorage.getItem("bitsentry-query-cache");
    if (cache === null || cache === "") {
      return;
    }

    try {
      const parsedCache = cachedStateSchema.parse(JSON.parse(cache));
      const updatedQueries = parsedCache.clientState.queries.filter(
        (q) =>
          !q.queryKey.every(
            (key: unknown, index: number) => key === queryKey[index],
          ),
      );
      parsedCache.clientState.queries = updatedQueries;
      localStorage.setItem(
        "bitsentry-query-cache",
        JSON.stringify(parsedCache),
      );
    } catch (error) {
      console.error("Failed to clear query cache:", error);
    }
  },

  // Get cache size
  getCacheSize: () => {
    const cache = localStorage.getItem("bitsentry-query-cache");
    if (cache === null || cache === "") {
      return 0;
    }

    return new Blob([cache]).size;
  },

  // Check if cache is stale
  isCacheStale: (timestamp: number, queryType: string) => {
    const staleTime = getStaleTime(queryType);
    return Date.now() - timestamp > staleTime;
  },
};
